import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { doc, onSnapshot, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const SLOT_COLORS = {
  empty:    { bg: '#F9FAFB', border: '#E5E7EB', text: '#9CA3AF' },
  occupied: { bg: '#F9FAFB', border: '#E5E7EB', text: '#374151' },
  drag:     { bg: '#EBF2FC', border: '#93C5FD', text: '#1A4A8A' },
};

function fullName(p) {
  return [p.firstName, p.surname].filter(Boolean).join(' ') || '—';
}

function StaffRoomsReadOnly({ staffRooms, staffAssignments }) {
  return (
    <div className="bg-white rounded-[14px] border border-[#E5E7EB] overflow-hidden">
      <div className="px-5 py-3 bg-[#F9FAFB] border-b border-[#E5E7EB]">
        <p className="text-[13px] font-bold text-[#111827]">MST Staff Rooms</p>
        <p className="text-[11.5px] text-[#9CA3AF] mt-0.5">Allocated by MST Sports Tours — read only</p>
      </div>
      <div className="p-5 space-y-3">
        {staffRooms.map((room) => {
          const occupants = staffAssignments[room.id] || [];
          return (
            <div key={room.id} className="border border-[#E5E7EB] rounded-[10px] p-3">
              <p className="text-[12px] font-bold text-[#111827] mb-1">{room.label}</p>
              {occupants.length === 0 ? (
                <p className="text-[11px] text-[#9CA3AF] italic">MST staff will be assigned</p>
              ) : (
                <div className="space-y-1">
                  {occupants.map((s) => (
                    <p key={s.id} className="text-[12px] text-[#374151]">{s.name}</p>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function RoomingAssignmentPage() {
  const [params] = useSearchParams();
  const token = params.get('token');

  const [state, setState]           = useState('loading');
  const [error, setError]           = useState('');
  const [tourName, setTourName]     = useState('');
  const [rooms, setRooms]           = useState([]);
  const [participants, setParticipants] = useState([]);
  const [assignments, setAssignments]   = useState({});
  const [draggedP, setDraggedP]     = useState(null);
  const [saving, setSaving]         = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [staffRooms, setStaffRooms] = useState([]);
  const [staffAssignments, setStaffAssignments] = useState({});
  const touchedRef = useRef(false); // coach has started placing people

  // ── Load the link doc ────────────────────────────────────────────────────
  // Everything comes from coachLinks/{token}: a trigger-maintained mirror of
  // the room layout and participant names, readable without signing in (see
  // functions/coachLinks.js). The tour's own docs are never read here.

  useEffect(() => {
    if (!token) { setState('error'); setError('No token found in this link.'); return undefined; }

    const unsub = onSnapshot(doc(db, 'coachLinks', token), (snap) => {
      if (!snap.exists() || snap.data().revoked || snap.data().kind !== 'rooming') {
        setState('error');
        setError('This link is invalid or has been replaced. Please ask MST for a new one.');
        return;
      }
      const link = snap.data();
      // The trigger fills the mirror a moment after the link is created.
      if (!link.mirroredAt) { setState('loading'); return; }
      setTourName(link.tourName || '');
      setRooms(link.rooms || []);
      setParticipants(link.participants || []);
      setStaffRooms(link.staffRooms || []);
      setStaffAssignments(link.staffAssignments || {});
      // Don't overwrite placements the coach is part-way through.
      if (!touchedRef.current) setAssignments(link.assignments || {});

      if (link.status === 'submitted' || link.status === 'finalised') {
        setAssignments(link.assignments || {});
        setSubmitted(true);
        setState('done');
        return;
      }
      if (link.status !== 'sent') {
        setState('error');
        setError('Room layout is not yet ready. MST will send you a link when it is.');
        return;
      }
      setState('ready');
    }, (err) => {
      console.error(err);
      setState('error');
      setError('Failed to load. Please try again.');
    });
    return unsub;
  }, [token]);

  // ── Drag & drop ──────────────────────────────────────────────────────────

  function handleDrop(roomId) {
    if (!draggedP || submitted) return;
    const room = rooms.find((r) => r.id === roomId);
    const current = assignments[roomId] || [];
    if (current.length >= (room?.beds || 0)) return;
    const newAssignments = {};
    Object.entries(assignments).forEach(([rid, pids]) => {
      newAssignments[rid] = pids.filter((pid) => pid !== draggedP.id);
    });
    newAssignments[roomId] = [...(newAssignments[roomId] || []), draggedP.id];
    touchedRef.current = true;
    setAssignments(newAssignments);
    setDraggedP(null);
  }

  function removeFromRoom(roomId, participantId) {
    if (submitted) return;
    touchedRef.current = true;
    setAssignments((prev) => ({
      ...prev,
      [roomId]: (prev[roomId] || []).filter((id) => id !== participantId),
    }));
  }

  // ── Submit ────────────────────────────────────────────────────────────────

  async function handleSubmit() {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'coachLinks', token), {
        assignments,
        status: 'submitted',
        coachUpdatedAt: serverTimestamp(),
      });
      setSubmitted(true);
      setState('done');
    } catch (err) {
      console.error(err);
      setSaving(false);
      alert('Failed to submit. Please try again.');
    }
  }

  // ── Derived ───────────────────────────────────────────────────────────────

  const assignedIds = new Set(Object.values(assignments).flat());
  const unassigned  = participants.filter((p) => !assignedIds.has(p.id));

  // ── Render states ─────────────────────────────────────────────────────────

  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <p className="text-[#9CA3AF] text-[14px]">Loading…</p>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4">
        <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 rounded-full bg-[#FEF2F2] flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-dr" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </div>
          <p className="text-[15px] font-bold text-[#111827] mb-2">Link not valid</p>
          <p className="text-[13px] text-[#6B7280]">{error}</p>
        </div>
      </div>
    );
  }

  if (state === 'done') {
    return (
      <div className="min-h-screen bg-[#F9FAFB] py-10 px-4">
        <div className="max-w-2xl mx-auto space-y-5">
          <div className="bg-[#0D2240] rounded-[12px] p-6 text-center">
            <p className="text-white font-bold text-[18px]">MST Sports Tours</p>
            <p className="text-[#93C5FD] text-[13px] mt-1">{tourName}</p>
          </div>
          <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-[#ECFDF5] flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-[#16A34A]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
            </div>
            <p className="text-[17px] font-bold text-[#111827] mb-2">Room assignments submitted</p>
            <p className="text-[13px] text-[#6B7280]">MST Sports Tours have been notified and will review your room assignments.</p>
          </div>
          {/* Read-only room view */}
          <div className="space-y-3">
            {rooms.map((room) => {
              const occupants = (assignments[room.id] || [])
                .map((pid) => participants.find((p) => p.id === pid))
                .filter(Boolean);
              return (
                <div key={room.id} className="bg-white rounded-[12px] border border-[#E5E7EB] overflow-hidden">
                  <div className="px-4 py-3 bg-[#F9FAFB] border-b border-[#E5E7EB] flex items-center justify-between">
                    <p className="text-[13px] font-bold text-[#111827]">{room.label}</p>
                    <p className="text-[11px] text-[#9CA3AF]">{room.beds} bed{room.beds !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="px-4 py-3">
                    {occupants.length === 0 ? (
                      <p className="text-[12px] text-[#9CA3AF] italic">Empty</p>
                    ) : (
                      <div className="space-y-1">
                        {occupants.map((p) => (
                          <p key={p.id} className="text-[13px] text-[#374151]">{fullName(p)}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {staffRooms.length > 0 && <StaffRoomsReadOnly staffRooms={staffRooms} staffAssignments={staffAssignments} />}
        </div>
      </div>
    );
  }

  // ── Main assignment UI ────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-10 px-4">
      <div className="max-w-2xl mx-auto space-y-5">

        {/* Header */}
        <div className="bg-[#0D2240] rounded-[12px] p-6 text-center">
          <p className="text-white font-bold text-[18px]">MST Sports Tours</p>
          <p className="text-[#93C5FD] text-[13px] mt-1">{tourName}</p>
          <p className="text-[#93C5FD] text-[12px] mt-0.5">Room Allocation</p>
        </div>

        {/* Instructions */}
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5">
          <p className="text-[14px] font-bold text-[#111827] mb-2">Assign participants to rooms</p>
          <p className="text-[13px] text-[#6B7280] leading-relaxed">
            Drag each participant from the list below into their room. Once everyone is assigned, click <strong>Submit</strong> to send your selections to MST.
          </p>
        </div>

        {/* Unassigned participants */}
        {unassigned.length > 0 && (
          <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5">
            <p className="text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-1">
              Unassigned participants
            </p>
            <p className="text-[11px] text-[#9CA3AF] mb-3">
              Tap a participant to select, then tap a room to place them. On desktop you can also drag.
            </p>
            <div className="flex flex-wrap gap-2">
              {unassigned.map((p) => {
                const isSelected = draggedP?.id === p.id;
                return (
                  <div
                    key={p.id}
                    draggable
                    onDragStart={() => setDraggedP(p)}
                    onDragEnd={() => setDraggedP(null)}
                    onClick={() => setDraggedP(isSelected ? null : p)}
                    className={`flex items-center gap-2 px-3 py-1.5 border rounded-full text-[13px] font-semibold cursor-pointer select-none transition-all ${
                      isSelected
                        ? 'bg-pb border-pb text-white scale-105 shadow-md'
                        : 'bg-[#EBF2FC] border-[#93C5FD] text-pb hover:scale-105'
                    }`}
                  >
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 ${isSelected ? 'bg-white text-pb' : 'bg-pb text-white'}`}>
                      {(p.firstName || '?').charAt(0)}
                    </span>
                    {fullName(p)}
                    {isSelected && <span className="text-[10px] opacity-80 ml-0.5">✓</span>}
                  </div>
                );
              })}
            </div>
            {draggedP && (
              <p className="text-[12px] text-pb font-semibold mt-3 animate-pulse">
                ↓ Now tap a room below to place <strong>{fullName(draggedP)}</strong>
              </p>
            )}
          </div>
        )}

        {/* Room cards */}
        {rooms.map((room) => {
          const occupantIds = assignments[room.id] || [];
          const occupants   = occupantIds.map((pid) => participants.find((p) => p.id === pid)).filter(Boolean);
          const isFull      = occupants.length >= room.beds;
          const isTarget    = draggedP && !isFull;

          return (
            <div
              key={room.id}
              onDragOver={isTarget ? (e) => e.preventDefault() : undefined}
              onDrop={isTarget ? () => handleDrop(room.id) : undefined}
              onClick={isTarget ? () => handleDrop(room.id) : undefined}
              className={`bg-white rounded-[14px] border-2 transition-all ${
                isTarget ? 'border-pb bg-[#EBF2FC]/30 cursor-pointer shadow-md' :
                isFull   ? 'border-[#E5E7EB] opacity-75' :
                           'border-[#E5E7EB]'
              }`}
            >
              <div className="px-5 py-3 border-b border-[#F1F3F6] flex items-center justify-between">
                <div>
                  <p className="text-[14px] font-bold text-[#111827]">{room.label}</p>
                  <p className="text-[11px] text-[#9CA3AF]">
                    {room.beds} bed{room.beds !== 1 ? 's' : ''} · {occupants.length}/{room.beds} assigned
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {isTarget && (
                    <span className="text-[11px] font-bold text-pb animate-pulse">Tap to place</span>
                  )}
                  <div className={`w-2.5 h-2.5 rounded-full transition-colors ${isFull ? 'bg-[#22C55E]' : occupants.length > 0 ? 'bg-[#F59E0B]' : 'bg-[#E5E7EB]'}`} />
                </div>
              </div>
              <div className="p-5 min-h-[64px]">
                {occupants.length === 0 ? (
                  <p className={`text-[13px] text-center py-1 ${isTarget ? 'text-pb font-semibold' : 'text-[#D1D5DB]'}`}>
                    {isTarget ? '+ Place here' : 'Empty'}
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {occupants.map((p) => (
                      <div key={p.id} className="flex items-center gap-1.5 px-2.5 py-1 bg-[#F9FAFB] border border-[#E5E7EB] rounded-full text-[13px] text-[#374151]">
                        <span className="w-4 h-4 rounded-full bg-[#6B7280] text-white flex items-center justify-center text-[8px] font-bold flex-shrink-0">
                          {(p.firstName || '?').charAt(0)}
                        </span>
                        {fullName(p)}
                        <button
                          onClick={(e) => { e.stopPropagation(); removeFromRoom(room.id, p.id); }}
                          className="text-[#9CA3AF] hover:text-dr ml-0.5 leading-none text-[15px]"
                          title="Remove from room"
                        >×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* MST Staff Rooms — read-only */}
        {staffRooms.length > 0 && <StaffRoomsReadOnly staffRooms={staffRooms} staffAssignments={staffAssignments} />}

        {/* Submit */}
        <div className="pb-8">
          <button
            onClick={handleSubmit}
            disabled={saving || unassigned.length > 0}
            className="w-full py-4 bg-pb text-white font-bold text-[15px] rounded-[12px] hover:bg-navy transition disabled:opacity-50"
          >
            {saving
              ? 'Submitting…'
              : unassigned.length > 0
                ? `${unassigned.length} participant${unassigned.length !== 1 ? 's' : ''} still unassigned`
                : 'Submit Room Assignments'}
          </button>
          {unassigned.length > 0 && (
            <p className="text-[12px] text-[#9CA3AF] text-center mt-2">All participants must be assigned before submitting.</p>
          )}
        </div>
      </div>
    </div>
  );
}
